import React, { useState, useEffect, useRef, useCallback } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { Capacitor } from '@capacitor/core';
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';
import { _T, C, CSS } from '../../shared/theme';
import { s_PB, s_GB, s_GC, s_TI } from '../../shared/styles';
import { t } from '../../shared/i18n';

const IS_NATIVE = Capacitor.isNativePlatform();

// Decode the JWT credential returned by Google without a library
function decodeGoogleJwt(token) {
  try {
    const payload = token.split('.')[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

const BASE = process.env.REACT_APP_API_URL || '/api';

// ── Nature ambient sound (Web Audio synthesis — no files needed) ──────────────
function useNatureAmbient() {
  const ctx    = useRef(null);
  const birds  = useRef([]);
  const nodes  = useRef([]);
  const active = useRef(false);

  const stop = useCallback(() => {
    birds.current.forEach(clearTimeout);
    birds.current = [];
    nodes.current.forEach(n => { try { n.stop?.(); } catch {} });
    nodes.current = [];
    try { ctx.current?.close(); } catch {}
    ctx.current = null;
    active.current = false;
  }, []);

  const start = useCallback(() => {
    if (active.current) return;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      active.current = true;
      const c = new AC();
      ctx.current = c;

      // Master gain with slow fade-in
      const master = c.createGain();
      master.gain.setValueAtTime(0, c.currentTime);
      master.gain.linearRampToValueAtTime(0.55, c.currentTime + 4);
      master.connect(c.destination);

      // ── Brown noise generator (wind base) ──────────────────────────────
      const mkBrown = (dur = 45) => {
        const buf  = c.createBuffer(1, c.sampleRate * dur, c.sampleRate);
        const data = buf.getChannelData(0);
        let last = 0;
        for (let i = 0; i < data.length; i++) {
          const w = Math.random() * 2 - 1;
          data[i] = (last + 0.02 * w) / 1.02;
          last = data[i];
          data[i] *= 3.5;
        }
        const src  = c.createBufferSource();
        src.buffer = buf; src.loop = true; return src;
      };

      // ── White noise generator (leaves / rain layer) ────────────────────
      const mkWhite = (dur = 30) => {
        const buf  = c.createBuffer(1, c.sampleRate * dur, c.sampleRate);
        const data = buf.getChannelData(0);
        for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
        const src  = c.createBufferSource();
        src.buffer = buf; src.loop = true; return src;
      };

      // ── Wind: brown noise → bandpass → gentle LFO on gain ─────────────
      const windSrc  = mkBrown();
      const windFilt = c.createBiquadFilter();
      windFilt.type = 'bandpass'; windFilt.frequency.value = 320; windFilt.Q.value = 0.6;
      const windGain = c.createGain(); windGain.gain.value = 0.22;
      const windLFO  = c.createOscillator();
      const lfoGain  = c.createGain();
      windLFO.frequency.value = 0.18; lfoGain.gain.value = 0.09;
      windLFO.connect(lfoGain); lfoGain.connect(windGain.gain);
      windSrc.connect(windFilt); windFilt.connect(windGain); windGain.connect(master);
      windLFO.start(); windSrc.start();
      nodes.current.push(windSrc, windLFO);

      // ── Leaves: white noise → highpass → fast tremolo ──────────────────
      const leafSrc  = mkWhite();
      const leafHP   = c.createBiquadFilter();
      leafHP.type = 'highpass'; leafHP.frequency.value = 3200;
      const leafGain = c.createGain(); leafGain.gain.value = 0.07;
      const leafLFO  = c.createOscillator();
      const leafLG   = c.createGain();
      leafLFO.frequency.value = 2.4; leafLG.gain.value = 0.05;
      leafLFO.connect(leafLG); leafLG.connect(leafGain.gain);
      leafSrc.connect(leafHP); leafHP.connect(leafGain); leafGain.connect(master);
      leafLFO.start(); leafSrc.start();
      nodes.current.push(leafSrc, leafLFO);

      // ── Distant stream: pink-ish noise → lowpass → very quiet ──────────
      const streamSrc  = mkWhite(20);
      const streamLP   = c.createBiquadFilter();
      streamLP.type = 'lowpass'; streamLP.frequency.value = 600;
      const streamGain = c.createGain(); streamGain.gain.value = 0.10;
      streamSrc.connect(streamLP); streamLP.connect(streamGain); streamGain.connect(master);
      streamSrc.start();
      nodes.current.push(streamSrc);

      // ── Bird calls: scheduled chirps ────────────────────────────────────
      // freq sweeps mimic real bird calls
      const BIRDS = [
        { f1: 3200, f2: 4100, dur: 0.18, vol: 0.13 },  // high warble
        { f1: 2400, f2: 1900, dur: 0.22, vol: 0.10 },  // descending call
        { f1: 1600, f2: 2200, dur: 0.14, vol: 0.09 },  // short whistle
        { f1: 2800, f2: 3600, dur: 0.20, vol: 0.11 },  // ascending tweet
        { f1: 1100, f2:  900, dur: 0.30, vol: 0.07 },  // low coo
      ];

      const chirp = (spec) => {
        if (!ctx.current || !active.current) return;
        const osc = ctx.current.createOscillator();
        const env = ctx.current.createGain();
        osc.type = 'sine';
        osc.connect(env); env.connect(master);
        const t = ctx.current.currentTime;
        osc.frequency.setValueAtTime(spec.f1, t);
        osc.frequency.exponentialRampToValueAtTime(spec.f2, t + spec.dur * 0.65);
        osc.frequency.exponentialRampToValueAtTime(spec.f1 * 0.85, t + spec.dur);
        env.gain.setValueAtTime(0, t);
        env.gain.linearRampToValueAtTime(spec.vol, t + 0.025);
        env.gain.setValueAtTime(spec.vol, t + spec.dur * 0.75);
        env.gain.linearRampToValueAtTime(0, t + spec.dur);
        osc.start(t); osc.stop(t + spec.dur + 0.05);

        // Sometimes add a quick second chirp (double-call pattern)
        if (Math.random() > 0.55) {
          const osc2 = ctx.current.createOscillator();
          const env2 = ctx.current.createGain();
          osc2.type = 'sine';
          osc2.connect(env2); env2.connect(master);
          const t2 = t + spec.dur + 0.08;
          osc2.frequency.setValueAtTime(spec.f2 * 0.95, t2);
          osc2.frequency.exponentialRampToValueAtTime(spec.f1 * 0.9, t2 + spec.dur * 0.8);
          env2.gain.setValueAtTime(0, t2);
          env2.gain.linearRampToValueAtTime(spec.vol * 0.7, t2 + 0.02);
          env2.gain.linearRampToValueAtTime(0, t2 + spec.dur * 0.8);
          osc2.start(t2); osc2.stop(t2 + spec.dur);
        }
      };

      const scheduleBird = () => {
        if (!active.current) return;
        const spec  = BIRDS[Math.floor(Math.random() * BIRDS.length)];
        const delay = 1800 + Math.random() * 4200; // 1.8 – 6s gap
        const tid = setTimeout(() => { chirp(spec); scheduleBird(); }, delay);
        birds.current.push(tid);
      };

      // Start first bird after 2s
      const initTid = setTimeout(scheduleBird, 2000);
      birds.current.push(initTid);

    } catch {}
  }, []);

  // Start on first interaction (browser autoplay policy)
  useEffect(() => {
    const resume = () => { start(); };
    document.addEventListener('click',     resume, { once: true, passive: true });
    document.addEventListener('touchstart', resume, { once: true, passive: true });
    document.addEventListener('keydown',   resume, { once: true });
    return () => {
      document.removeEventListener('click',     resume);
      document.removeEventListener('touchstart', resume);
      document.removeEventListener('keydown',   resume);
      stop();
    };
  }, [start, stop]);
}

export default function GoogleAuthScreen({ lang, onDone, initialMode }) {
  const nl = lang === 'nl';
  useNatureAmbient(); // 🌿 nature sounds while on login screen
  const [mode, setMode]       = useState(initialMode || 'social'); // 'social'|'login'|'register'|'forgot'|'reset'
  const [error, setError]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [benefitIdx, setBenefitIdx] = useState(0);
  const benefitTouch = useRef(null);

  useEffect(() => {
    const t = setInterval(() => setBenefitIdx(i => (i + 1) % 3), 3200);
    return () => clearInterval(t);
  }, []);

  // Cycling hero headline
  const HERO_PHRASES = [
    'Master your Senses',
    'Become a Sensei',
    'Gain Clarity with\nFocus and Balance',
  ];
  const [hIdx,   setHIdx]   = useState(0);
  const [hPhase, setHPhase] = useState('in'); // 'in' | 'out'

  useEffect(() => {
    const SHOW_MS = 3000;  // longer to let all words land before dissolve
    const OUT_MS  = 700;   // blur dissolve duration
    if (hPhase === 'in') {
      const t = setTimeout(() => setHPhase('out'), SHOW_MS);
      return () => clearTimeout(t);
    } else {
      const t = setTimeout(() => {
        setHIdx(i => (i + 1) % HERO_PHRASES.length);
        setHPhase('in');
      }, OUT_MS);
      return () => clearTimeout(t);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hPhase]);

  // Initialize Google Auth once on mount (native only)
  useEffect(() => {
    if (!IS_NATIVE) return;
    GoogleAuth.initialize({
      clientId: '1077804150786-2e6uf8lkh7hauhsboie5suo95hl90l3h.apps.googleusercontent.com',
      scopes: ['profile', 'email'],
      grantOfflineAccess: true,
    }).catch(() => {});
  }, []);

  // Email form state
  const [name, setName]         = useState('');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [resetToken, setResetToken] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get('reset') || '';
  });

  // ── Google (shared finish) ─────────────────────────────────────────
  const finishGoogle = ({ sub, email, name, given_name, picture }) => {
    try {
      localStorage.setItem('sl_google_id',      sub     || '');
      localStorage.setItem('sl_google_email',   email   || '');
      localStorage.setItem('sl_google_picture', picture || '');
      localStorage.setItem('sl_auth_method',    'google');
    } catch {}
    onDone({
      name:     given_name || name || '',
      email:    email      || '',
      picture:  picture    || null,
      googleId: sub        || '',
    });
  };

  // ── Google (web popup) ────────────────────────────────────────────
  const handleGoogleSuccess = (credentialResponse) => {
    setLoading(true);
    const payload = decodeGoogleJwt(credentialResponse.credential);
    if (payload) finishGoogle(payload);
    else { setError('Could not retrieve profile. Please try again.'); setLoading(false); }
  };
  const handleGoogleError = () => {
    setError('Google sign-in failed. Check your connection and try again.');
  };

  // ── Google (native — Capacitor) ───────────────────────────────────
  const handleNativeGoogle = async () => {
    setError(null);
    setLoading(true);
    try {
      const user = await GoogleAuth.signIn();
      finishGoogle({
        sub:        user.id            || '',
        email:      user.email         || '',
        name:       user.name          || '',
        given_name: user.givenName     || '',
        picture:    user.imageUrl      || '',
      });
    } catch (e) {
      const msg = e?.message || e?.code || String(e) || '';
      console.error('[GoogleAuth] signIn error:', msg);
      if (msg.includes('12501') || msg.toLowerCase().includes('cancel')) {
        // User cancelled — silent
        setLoading(false);
        return;
      }
      if (msg.includes('10:') || msg.includes('DEVELOPER_ERROR')) {
        setError('Configuratiefout: SHA-1 of client ID klopt niet. Controleer Firebase.');
      } else if (msg.includes('7:') || msg.toLowerCase().includes('network')) {
        setError('Geen internetverbinding. Probeer opnieuw.');
      } else {
        setError('Google inloggen mislukt (' + msg + ')');
      }
      setLoading(false);
    }
  };

  // ── Email register ─────────────────────────────────────────────────
  const handleRegister = async (e) => {
    e.preventDefault();
    setError(null);
    if (!name.trim() || !email.trim() || !password) {
      setError(nl ? 'Vul alle velden in.' : 'Please fill in all fields.');
      return;
    }
    if (password.length < 6) {
      setError(nl ? 'Wachtwoord minimaal 6 tekens.' : 'Password must be at least 6 characters.');
      return;
    }
    setLoading(true);
    try {
      const existingUserId = localStorage.getItem('sl_user_id') || undefined;
      let inviteToken;
      try { inviteToken = JSON.parse(localStorage.getItem('sl_contact_invite') || 'null')?.token; } catch {}
      const res  = await fetch(`${BASE}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), email: email.trim(), password, existingUserId, inviteToken }),
      });
      const data = await res.json();
      if (!res.ok) {
        const msg = data.error === 'email already registered'
          ? (nl ? 'E-mail is al geregistreerd.' : 'Email already registered.')
          : (data.error || (nl ? 'Registratie mislukt.' : 'Registration failed.'));
        setError(msg);
        setLoading(false);
        return;
      }
      localStorage.setItem('sl_user_id',   data.id);
      localStorage.setItem('sl_name',      JSON.stringify(name.trim()));
      localStorage.setItem('sl_auth_method', 'email');
      localStorage.removeItem('sl_contact_invite'); // used in the register call — clear so it isn't reused
      onDone({ name: name.trim(), email: email.trim(), emailAuth: true });
    } catch {
      setError(nl ? 'Verbindingsfout. Probeer opnieuw.' : 'Connection error. Please try again.');
      setLoading(false);
    }
  };

  // ── Forgot password ────────────────────────────────────────────────
  const handleForgot = async (e) => {
    e.preventDefault();
    setError(null);
    if (!email.trim()) {
      setError(nl ? 'Vul je e-mailadres in.' : 'Please enter your email address.');
      return;
    }
    setLoading(true);
    try {
      await fetch(`${BASE}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      setSuccess(nl
        ? 'Als dit e-mailadres bekend is, ontvang je een link om je wachtwoord te herstellen.'
        : 'If this email is registered, you will receive a reset link shortly.');
      setLoading(false);
    } catch {
      setError(nl ? 'Verbindingsfout. Probeer opnieuw.' : 'Connection error. Please try again.');
      setLoading(false);
    }
  };

  // ── Reset password ─────────────────────────────────────────────────
  const handleReset = async (e) => {
    e.preventDefault();
    setError(null);
    if (!password || password.length < 6) {
      setError(nl ? 'Wachtwoord minimaal 6 tekens.' : 'Password must be at least 6 characters.');
      return;
    }
    setLoading(true);
    try {
      const res  = await fetch(`${BASE}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, password }),
      });
      if (!res.ok) {
        setError(nl ? 'Ongeldige of verlopen link. Vraag een nieuwe aan.' : 'Invalid or expired link. Please request a new one.');
        setLoading(false);
        return;
      }
      setSuccess(nl ? 'Wachtwoord ingesteld! Je kunt nu inloggen.' : 'Password updated! You can now sign in.');
      setLoading(false);
      // Clear token from URL
      window.history.replaceState({}, '', window.location.pathname);
      setTimeout(() => { setMode('login'); setSuccess(null); setPassword(''); }, 2000);
    } catch {
      setError(nl ? 'Verbindingsfout. Probeer opnieuw.' : 'Connection error. Please try again.');
      setLoading(false);
    }
  };

  // ── Email login ────────────────────────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    if (!email.trim() || !password) {
      setError(nl ? 'Vul e-mail en wachtwoord in.' : 'Please enter email and password.');
      return;
    }
    setLoading(true);
    try {
      const res  = await fetch(`${BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(nl ? 'Onjuist e-mail of wachtwoord.' : 'Incorrect email or password.');
        setLoading(false);
        return;
      }
      localStorage.setItem('sl_user_id',   data.id);
      localStorage.setItem('sl_name',      JSON.stringify(data.name));
      localStorage.setItem('sl_auth_method', 'email');
      onDone({ name: data.name, email: email.trim(), emailAuth: true });
    } catch {
      setError(nl ? 'Verbindingsfout. Probeer opnieuw.' : 'Connection error. Please try again.');
      setLoading(false);
    }
  };

  // ── Shared wrapper ─────────────────────────────────────────────────
  const isSocial = mode === 'social';
  return (
    <div style={{
      minHeight: '100vh',
      background: `linear-gradient(160deg,${_T.bg},${_T.bg2})`,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center',
      justifyContent: isSocial ? 'flex-start' : 'center',
      padding: isSocial ? '28px 20px 48px' : '32px 24px',
      overflowY: 'auto',
    }}>
      <style>{CSS + `
        @keyframes sl-auth-glow{0%,100%{opacity:.4}50%{opacity:.8}}
        @keyframes sl-win {from{opacity:0;transform:translateY(18px) scale(.94)}to{opacity:1;transform:translateY(0) scale(1)}}
        @keyframes sl-wout{from{opacity:1;filter:blur(0px)}to{opacity:0;filter:blur(10px)}}
        @keyframes sl-mood{from{opacity:0}to{opacity:1}}
      `}</style>

      {/* Ambient pulsing glow (base layer — mood overlay added per-phrase in hero block) */}
      {isSocial && (
        <div style={{ position:'fixed', top:0, left:'50%', transform:'translateX(-50%)', width:500, height:260, borderRadius:'0 0 50% 50%', background:`radial-gradient(ellipse,${_T.accent}10 0%,transparent 70%)`, pointerEvents:'none', animation:'sl-auth-glow 5s ease-in-out infinite' }} />
      )}

      {/* Logo */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', marginBottom: isSocial ? 14 : 24, position:'relative' }}>
        <img src='https://senselifting.com/3d_logo.png' style={{ width: isSocial ? 120 : 140 }} alt="Sense Lifting" />
      </div>

      {/* ── Mode: social — conversion landing ── */}
      {isSocial && (
        <div style={{ width:'100%', maxWidth:360 }}>

          {/* Hero headline — 3-phrase loop, word-by-word stagger */}
          {(() => {
            // Per-phrase mood colour for ambient glow behind logo
            const MOOD_COLORS = [
              'rgba(120,80,220,0.22)',   // indigo/purple — Master your Senses
              'rgba(220,170,40,0.20)',   // amber/gold    — Everybody becomes a Sensei
              'rgba(30,180,140,0.20)',   // teal/green    — Gain Clarity…
            ];
            const moodColor = MOOD_COLORS[hIdx];
            return (
              <div style={{ position:'relative' }}>
                {/* mood glow — transitions colour per phrase */}
                <div key={`mood-${hIdx}`} style={{
                  position:'fixed', top:0, left:'50%', transform:'translateX(-50%)',
                  width:480, height:280, borderRadius:'0 0 50% 50%',
                  background:`radial-gradient(ellipse,${moodColor} 0%,transparent 70%)`,
                  pointerEvents:'none', zIndex:0,
                  animation:'sl-mood 1.2s ease both',
                }} />
              </div>
            );
          })()}
          <div style={{ textAlign:'center', marginBottom:20, minHeight:80 }}>
            {/* Word-by-word stagger container */}
            <div
              key={`h-${hIdx}-${hPhase}`}
              style={{
                fontFamily: 'Georgia,serif',
                fontSize: hIdx === 2 ? 21 : 26,
                fontWeight: hIdx === 1 ? 600 : 700,
                color: hIdx === 1 ? _T.accent : '#fff',
                lineHeight: 1.4,
                marginBottom: 14,
                letterSpacing: hIdx === 1 ? 0.1 : -0.3,
                fontStyle: hIdx === 1 ? 'italic' : 'normal',
              }}
            >
              {/* Split on spaces and newlines, preserve line breaks */}
              {HERO_PHRASES[hIdx].split('\n').map((line, li, lines) => (
                <span key={li} style={{ display:'block' }}>
                  {line.split(' ').map((word, wi, words) => {
                    // cumulative word index for delay
                    const prevWords = HERO_PHRASES[hIdx].split('\n').slice(0, li).join(' ').split(' ').filter(Boolean).length;
                    const wordIdx = prevWords + wi;
                    const stagger = wordIdx * 110;
                    return (
                      <span key={wi} style={{
                        display: 'inline-block',
                        marginRight: wi < words.length - 1 ? '0.28em' : 0,
                        animation: hPhase === 'in'
                          ? `sl-win 0.62s ${stagger}ms cubic-bezier(.22,1,.36,1) both`
                          : `sl-wout 0.44s ${stagger * 0.4}ms ease both`,
                      }}>
                        {word}
                      </span>
                    );
                  })}
                </span>
              ))}
            </div>
            <div style={{ fontSize:11, color:`${_T.accent}66`, fontWeight:700, letterSpacing:2, textTransform:'uppercase' }}>
              {nl ? 'AI · Training · Mentale kracht · Community' : 'AI · Training · Mental fitness · Community'}
            </div>
          </div>

          {/* 3 benefit cards — looped carousel */}
          {(() => {
            const BENEFITS = [
              { icon:'🧠', en:['AI coaching built for you','Personalised insights that adapt to your body, goals and lifestyle.'], nl:['AI-coaching op maat','Gepersonaliseerde inzichten die zich aanpassen aan jouw lichaam en doelen.'] },
              { icon:'⚡', en:['Train smarter, recover faster','Exercises, mental games, sleep tracking and stress analysis — all in one.'], nl:['Slimmer trainen, sneller herstellen','Oefeningen, games, slaap en stressanalyse — alles in één app.'] },
              { icon:'🌍', en:['A community that lifts you up','Connect, compete and grow with thousands of health-focused people.'], nl:['Een community die je omhoog tilt','Verbind, daag uit en groei met duizenden gezondheidsmensen.'] },
            ];
            const f = BENEFITS[benefitIdx];
            return (
              <div
                style={{ marginBottom:18, userSelect:'none' }}
                onTouchStart={e => { benefitTouch.current = e.touches[0].clientX; }}
                onTouchEnd={e => {
                  if (benefitTouch.current === null) return;
                  const dx = e.changedTouches[0].clientX - benefitTouch.current;
                  if (Math.abs(dx) > 40) setBenefitIdx(i => (i + (dx < 0 ? 1 : -1) + 3) % 3);
                  benefitTouch.current = null;
                }}
              >
                <div style={{ display:'flex', gap:12, background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:14, padding:'14px 16px', alignItems:'flex-start', minHeight:72, transition:'opacity 0.25s' }}>
                  <span style={{ fontSize:26, flexShrink:0, lineHeight:1.2, marginTop:1 }}>{f.icon}</span>
                  <div>
                    <div style={{ color:'#fff', fontSize:13, fontWeight:700, marginBottom:3 }}>{nl ? f.nl[0] : f.en[0]}</div>
                    <div style={{ color:C.silver, fontSize:11, lineHeight:1.55 }}>{nl ? f.nl[1] : f.en[1]}</div>
                  </div>
                </div>
                <div style={{ display:'flex', justifyContent:'center', gap:6, marginTop:8 }}>
                  {[0,1,2].map(i => (
                    <div key={i} onClick={() => setBenefitIdx(i)} style={{
                      width: i === benefitIdx ? 16 : 5, height:5, borderRadius:3,
                      background: i === benefitIdx ? _T.accent : 'rgba(255,255,255,0.18)',
                      cursor:'pointer', transition:'width 0.3s,background 0.3s',
                    }} />
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Trust / social proof strip */}
          <div style={{ display:'flex', justifyContent:'center', gap:6, marginBottom:18, flexWrap:'wrap' }}>
            {[
              nl ? '✓ Gratis starten' : '✓ Free to start',
              nl ? '🔒 Privé & veilig' : '🔒 Private & secure',
              nl ? '🚫 Geen reclame' : '🚫 No ads ever',
            ].map((chip,i) => (
              <div key={i} style={{ background:`${_T.accent}12`, border:`1px solid ${_T.accent}30`, borderRadius:20, padding:'4px 12px', fontSize:11, color:_T.accent, fontWeight:600 }}>
                {chip}
              </div>
            ))}
          </div>

          {/* Primary sign-in card */}
          <div style={{ ...s_GC({ padding:'22px 20px' }), width:'100%', textAlign:'center', marginBottom:12 }}>
            <div style={{ fontSize:15, fontWeight:800, color:'#fff', marginBottom:4 }}>
              {nl ? 'Start vandaag gratis' : 'Start free today'}
            </div>
            <div style={{ fontSize:11, color:C.silver, marginBottom:16 }}>
              {nl ? 'Geen creditcard · Klaar in 10 seconden' : 'No credit card · Ready in 10 seconds'}
            </div>

            {loading ? (
              <div style={{ color:C.silver, fontSize:14, padding:'12px 0' }}>
                <span className="spin" style={{ display:'inline-block', marginRight:8 }}>⟳</span>
                {t('auth.signingin', lang)}
              </div>
            ) : IS_NATIVE ? (
              <button onClick={handleNativeGoogle} style={s_PB({ width:'100%', fontSize:14, padding:'14px 20px' })}>
                🔵 {nl ? 'Gratis starten met Google' : 'Start free with Google'}
              </button>
            ) : (
              <div style={{ display:'flex', justifyContent:'center' }}>
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  useOneTap={false}
                  shape="pill" size="large" text="continue_with" locale={lang}
                />
              </div>
            )}

            {error && <div style={{ color:C.red, fontSize:12, marginTop:10, lineHeight:1.5 }}>⚠ {error}</div>}
          </div>

          {/* Divider + email options */}
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
            <div style={{ flex:1, height:1, background:`${C.silver}22` }}/>
            <div style={{ color:C.silver, fontSize:11 }}>{nl ? 'of ga door met e-mail' : 'or continue with email'}</div>
            <div style={{ flex:1, height:1, background:`${C.silver}22` }}/>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            <button style={s_PB({ width:'100%', fontSize:13, padding:'12px 20px' })} onClick={() => { setMode('register'); setError(null); }}>
              ✉️ {nl ? 'Account aanmaken met e-mail' : 'Create account with email'}
            </button>
            <button style={s_GB({ width:'100%', fontSize:13, padding:'12px 20px' })} onClick={() => { setMode('login'); setError(null); }}>
              {nl ? 'Al lid? Inloggen →' : 'Already a member? Sign in →'}
            </button>
          </div>
        </div>
      )}

      {/* Compact heading for non-social modes */}
      {!isSocial && (
        <>
          <div style={{ fontFamily:'Georgia,serif', fontSize:22, color:C.white, marginBottom:6, textAlign:'center' }}>
            {t('auth.welcome', lang)}
          </div>
          <div style={{ color:_T.accent, fontSize:12, fontWeight:700, marginBottom:24, textAlign:'center', letterSpacing:0.5 }}>
            {t('auth.subtitle', lang)}
          </div>
        </>
      )}

      {/* ── Mode: register ── */}
      {mode === 'register' && (
        <div style={{ width: '100%', maxWidth: 340 }}>
          <div style={{ ...s_GC({ padding: '24px 20px' }), width: '100%' }}>
            <div style={{ fontSize: 11, color: _T.accent, letterSpacing: 3, fontWeight: 600, marginBottom: 18 }}>
              {nl ? 'GRATIS ACCOUNT' : 'CREATE FREE ACCOUNT'}
            </div>
            <form onSubmit={handleRegister} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <input
                type="text" placeholder={nl ? 'Jouw naam' : 'Your name'}
                value={name} onChange={e => setName(e.target.value)}
                style={s_TI({ width: '100%', boxSizing: 'border-box' })}
                autoComplete="name"
              />
              <input
                type="email" placeholder={nl ? 'E-mailadres' : 'Email address'}
                value={email} onChange={e => setEmail(e.target.value)}
                style={s_TI({ width: '100%', boxSizing: 'border-box' })}
                autoComplete="email"
              />
              <input
                type="password" placeholder={nl ? 'Wachtwoord (min. 6 tekens)' : 'Password (min. 6 characters)'}
                value={password} onChange={e => setPassword(e.target.value)}
                style={s_TI({ width: '100%', boxSizing: 'border-box' })}
                autoComplete="new-password"
              />
              {error && <div style={{ color: C.red, fontSize: 12, lineHeight: 1.5 }}>⚠ {error}</div>}
              <button type="submit" disabled={loading} style={s_PB({ width: '100%', fontSize: 14, padding: '13px 20px', marginTop: 4 })}>
                {loading
                  ? (nl ? 'Bezig...' : 'Creating account...')
                  : (nl ? 'Account aanmaken →' : 'Create account →')}
              </button>
            </form>
          </div>
          <button onClick={() => { setMode('social'); setError(null); }} style={{ background: 'none', border: 'none', color: C.silver, fontSize: 13, cursor: 'pointer', marginTop: 16, width: '100%', textAlign: 'center' }}>
            ← {nl ? 'Terug' : 'Back'}
          </button>
          <div style={{ color: C.silver, fontSize: 12, marginTop: 12, textAlign: 'center' }}>
            {nl ? 'Al een account? ' : 'Already have an account? '}
            <span onClick={() => { setMode('login'); setError(null); }} style={{ color: _T.accent, cursor: 'pointer', textDecoration: 'underline' }}>
              {nl ? 'Inloggen' : 'Sign in'}
            </span>
          </div>
        </div>
      )}

      {/* ── Mode: login ── */}
      {mode === 'login' && (
        <div style={{ width: '100%', maxWidth: 340 }}>
          <div style={{ ...s_GC({ padding: '24px 20px' }), width: '100%' }}>
            <div style={{ fontSize: 11, color: _T.accent, letterSpacing: 3, fontWeight: 600, marginBottom: 18 }}>
              {nl ? 'INLOGGEN' : 'SIGN IN'}
            </div>
            <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <input
                type="email" placeholder={nl ? 'E-mailadres' : 'Email address'}
                value={email} onChange={e => setEmail(e.target.value)}
                style={s_TI({ width: '100%', boxSizing: 'border-box' })}
                autoComplete="email"
              />
              <input
                type="password" placeholder={nl ? 'Wachtwoord' : 'Password'}
                value={password} onChange={e => setPassword(e.target.value)}
                style={s_TI({ width: '100%', boxSizing: 'border-box' })}
                autoComplete="current-password"
              />
              {error && <div style={{ color: C.red, fontSize: 12, lineHeight: 1.5 }}>⚠ {error}</div>}
              <button type="submit" disabled={loading} style={s_PB({ width: '100%', fontSize: 14, padding: '13px 20px', marginTop: 4 })}>
                {loading
                  ? (nl ? 'Bezig...' : 'Signing in...')
                  : (nl ? 'Inloggen →' : 'Sign in →')}
              </button>
            </form>
          </div>
          <button onClick={() => { setMode('social'); setError(null); }} style={{ background: 'none', border: 'none', color: C.silver, fontSize: 13, cursor: 'pointer', marginTop: 16, width: '100%', textAlign: 'center' }}>
            ← {nl ? 'Terug' : 'Back'}
          </button>
          <div style={{ color: C.silver, fontSize: 12, marginTop: 12, textAlign: 'center' }}>
            {nl ? 'Nog geen account? ' : 'No account yet? '}
            <span onClick={() => { setMode('register'); setError(null); }} style={{ color: _T.accent, cursor: 'pointer', textDecoration: 'underline' }}>
              {nl ? 'Aanmelden' : 'Sign up for free'}
            </span>
          </div>
          <div style={{ textAlign: 'center', marginTop: 10 }}>
            <span onClick={() => { setMode('forgot'); setError(null); setSuccess(null); }} style={{ color: C.silver, fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>
              {nl ? 'Wachtwoord vergeten?' : 'Forgot password?'}
            </span>
          </div>
        </div>
      )}

      {/* ── Mode: forgot ── */}
      {mode === 'forgot' && (
        <div style={{ width: '100%', maxWidth: 340 }}>
          <div style={{ ...s_GC({ padding: '24px 20px' }), width: '100%' }}>
            <div style={{ fontSize: 11, color: _T.accent, letterSpacing: 3, fontWeight: 600, marginBottom: 18 }}>
              {nl ? 'WACHTWOORD VERGETEN' : 'FORGOT PASSWORD'}
            </div>
            {success ? (
              <div style={{ color: '#4AC87A', fontSize: 13, lineHeight: 1.6, padding: '8px 0' }}>✓ {success}</div>
            ) : (
              <form onSubmit={handleForgot} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ color: C.silver, fontSize: 12, lineHeight: 1.6, marginBottom: 4 }}>
                  {nl ? 'Vul je e-mailadres in en ontvang een herstelLink.' : 'Enter your email to receive a reset link.'}
                </div>
                <input
                  type="email" placeholder={nl ? 'E-mailadres' : 'Email address'}
                  value={email} onChange={e => setEmail(e.target.value)}
                  style={s_TI({ width: '100%', boxSizing: 'border-box' })}
                  autoComplete="email"
                />
                {error && <div style={{ color: C.red, fontSize: 12, lineHeight: 1.5 }}>⚠ {error}</div>}
                <button type="submit" disabled={loading} style={s_PB({ width: '100%', fontSize: 14, padding: '13px 20px', marginTop: 4 })}>
                  {loading ? (nl ? 'Bezig...' : 'Sending...') : (nl ? 'Stuur herstelLink →' : 'Send reset link →')}
                </button>
              </form>
            )}
          </div>
          <button onClick={() => { setMode('login'); setError(null); setSuccess(null); }} style={{ background: 'none', border: 'none', color: C.silver, fontSize: 13, cursor: 'pointer', marginTop: 16, width: '100%', textAlign: 'center' }}>
            ← {nl ? 'Terug naar inloggen' : 'Back to sign in'}
          </button>
        </div>
      )}

      {/* ── Mode: reset (via email link ?reset=token) ── */}
      {mode === 'reset' && (
        <div style={{ width: '100%', maxWidth: 340 }}>
          <div style={{ ...s_GC({ padding: '24px 20px' }), width: '100%' }}>
            <div style={{ fontSize: 11, color: _T.accent, letterSpacing: 3, fontWeight: 600, marginBottom: 18 }}>
              {nl ? 'NIEUW WACHTWOORD' : 'NEW PASSWORD'}
            </div>
            {success ? (
              <div style={{ color: '#4AC87A', fontSize: 13, lineHeight: 1.6, padding: '8px 0' }}>✓ {success}</div>
            ) : (
              <form onSubmit={handleReset} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <input
                  type="password" placeholder={nl ? 'Nieuw wachtwoord (min. 6 tekens)' : 'New password (min. 6 characters)'}
                  value={password} onChange={e => setPassword(e.target.value)}
                  style={s_TI({ width: '100%', boxSizing: 'border-box' })}
                  autoComplete="new-password"
                />
                {error && <div style={{ color: C.red, fontSize: 12, lineHeight: 1.5 }}>⚠ {error}</div>}
                <button type="submit" disabled={loading} style={s_PB({ width: '100%', fontSize: 14, padding: '13px 20px', marginTop: 4 })}>
                  {loading ? (nl ? 'Opslaan...' : 'Saving...') : (nl ? 'Wachtwoord instellen →' : 'Set new password →')}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      <div style={{ marginTop: 24, textAlign:'center' }}>
        {!isSocial && (
          <div style={{ color:C.silver, fontSize:11, maxWidth:300, lineHeight:1.6, marginBottom:12 }}>
            {nl ? 'We gebruiken alleen je naam en e-mail — geen advertenties, geen dataverkoop.' : 'We only use your name and email — no ads, no data selling.'}
          </div>
        )}
        <div style={{ display:'flex', justifyContent:'center', gap:20 }}>
          <a href="/privacy" style={{ color:'#3a4a62', fontSize:11, textDecoration:'none' }}>Privacy Policy</a>
          <a href="/terms"   style={{ color:'#3a4a62', fontSize:11, textDecoration:'none' }}>Terms &amp; Conditions</a>
        </div>
      </div>
    </div>
  );
}
